import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

export interface Size {
  width: number;
  height: number;
}

/**
 * Dimensioni reali di un elemento, aggiornate con `ResizeObserver`.
 *
 * Serve agli SVG che costruiscono il `viewBox` in pixel reali (scala 1:1)
 * invece di riscalare un disegno fisso, così i testi non si deformano. In
 * render statico, o finché l'elemento non è montato, vale `fallback`.
 */
export function useElementSize<T extends Element>(fallback: Size): [RefObject<T | null>, Size] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<Size>(fallback);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = (): void => {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) setSize({ width: rect.width, height: rect.height });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  return [ref, size];
}
